import { panel, PropertiesPanel, PropertiesPanelPickerOption, PropertiesPanelSection } from '../../../../../widgets/propertiesPanel';
import { Spinner } from '../../../../../widgets/spinner';
import { useDialogManager } from '../../../../../widgets/dialog';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useMaterialsPropertiesCreator } from 'src/components/pages/preview3D/editors/editMaterialsEditor/editMaterialsEditorConfig';
import usePreview3DStore from 'src/components/widgets/preview3D/store';
import UpgradeDialog from 'src/components/widgets/upgradeDialog/upgradeDialog';
import { stringFormat, T } from 'src/shared/helpers';
import { useSubscriptionData } from 'src/shared/hooks/useSubscriptionData';
import styled from 'styled-components';
import InspectorShell, { InspectorUsage } from './inspectorShell';
import { CamerasIcon, MeshNodeIcon } from '../../../../../widgets/icons/scene';
import { showErrorNotification } from '../../../../../widgets/notificationToaster';
import { captureMaterialPreview, materialSwatchFallback, readMaterialPreview, saveMaterialPreview } from './materialPreview';
import { sceneAssetUrl } from './sceneUrls';
import { buildPbrMaterialPanel } from './buildPbrMaterialPanel';
import { buildPbrExtraProperties } from './pbrExtraProperties';
import { FinishMode } from './pbrMaterialLayout';
import { ChevronIcon } from '../../../../../widgets/icons/scene';

const generateTextureUniqueId = <T extends BABYLON.Material>(material: T, type: string, mobile: boolean) => {
    return [material.id, type, mobile ? 'mobile' : 'default'].join('_');
};

interface MaterialOverlaySimpleProps {
    material: any; // Il materiale BABYLON selezionato
    onMaterialUpdated?: (material: any) => void; // Callback per notificare aggiornamenti del materiale
    onNameChanged?: (newName: string) => void; // Callback per notificare cambio nome specificatamente
    additionalFeatures?: any;
    onClose?: () => void; // Callback per chiudere l'overlay
    /** L'anteprima è appena stata riscattata: la lista che ci ha aperti può mostrarla subito. */
    onPreviewUpdated?: (materialID: string, dataUrl: string) => void;
    /** Porta la colonna di sinistra sugli oggetti e apre quello scelto: vedi la pastiglia. */
    onRevealObject?: (meshID: string) => void;
    /** La scena è cambiata con un gesto che non passa da un campo: chi ci sta sopra segni che c'è da salvare. */
    onSceneChanged?: () => void;
}

const UnsupportedNote = styled.p`
    padding: 20px;
    color: ${(props) => props.theme.app.primary};
    font-size: 12px;
    font-style: italic;
    text-align: center;
`;

// IL VELO SUL RITRATTO. Nero e glifo bianco in tutti e due i temi, e non è una dimenticanza:
// sotto c'è una fotografia, che chiara o scura resta quella — un velo che seguisse il tema
// sparirebbe sulla metà delle sfere.
const PreviewShot = styled.span`
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.45);
    color: #fff;
    opacity: 0;
    transition: opacity 120ms ease;

    svg {
        width: 16px;
        height: 16px;
    }
`;

// IL RITRATTO IN TESTATA È ANCHE IL COMANDO, e sono due cose che si sono unite. La faccia del
// materiale stava due volte nello stesso pannello — una pallina tinta accanto al nome e la sfera
// vera nella riga sotto — e quella accanto al nome, cioè il posto dove si guarda per capire che
// cosa si sta toccando, era un segnaposto.
//
// Rifare la fotografia è un gesto raro — lo si fa dopo aver cambiato il materiale — e una riga
// con etichetta lo annunciava a ogni apertura, prima ancora del colore di base: il posto più in
// vista per la cosa che si fa meno. Adesso il gesto sta sulla cosa: si preme la sfera. Che si
// possa premere lo dice il velo, che compare passandoci sopra e resta acceso mentre scatta — è
// anche l'unico segno che sta succedendo.
const PreviewThumb = styled.button<{ $image: string | null; $color?: string | null }>`
    position: relative;
    flex: none;
    width: 34px;
    height: 34px;
    padding: 0;
    cursor: pointer;
    border-radius: 50%;
    border: 1px solid ${(props) => props.theme.app.borderColor};
    background-color: ${(props) => props.theme.app.pageBackgroundColor};
    background-image: ${(props) => (props.$image ? `url(${props.$image})` : materialSwatchFallback(props.$color))};
    background-position: center;
    background-size: cover;

    &:not(:disabled):hover ${PreviewShot}, &:focus-visible ${PreviewShot}, &[data-busy='true'] ${PreviewShot} {
        opacity: 1;
    }

    &:disabled {
        cursor: default;
    }
`;

const UpgradeBanner = styled(UpgradeDialog)`
    margin-bottom: 20px;
    p {
        margin: 0;
        padding: 0;
        line-height: 18px;
    }
    z-index: 10000 !important;
`;

// Il titolo di una SEZIONE non si apre e non si chiude (è la regola del prototipo): una sezione
// che si chiude nasconderebbe metà materiale dietro una maniglia che nessuno ha ragione di
// premere. A chiudersi sono i gruppi dentro di lei, che è il livello a cui la scelta serve.
// IL CAPITOLO NON SI DISEGNA PIÙ QUI. Era una `h4` scritta a mano, uguale a quella che World
// ed Effects si scrivevano per conto loro: tre copie della stessa parola in alto, con tre
// margini che avevano già cominciato a divergere. Ora è `PropertiesPanelSection`.

// LA SPINA «ADVANCED» NON È UN ACCORDION, ED È UNA SCELTA DI SOSTANZA. L'accordion di questo
// backoffice tiene il corpo dentro un contenitore alto al massimo 3000px con `overflow: hidden`:
// dieci gruppi aperti là dentro si troverebbero il fondo tagliato, senza niente che lo dica.
// Qui la spina decide **se** i gruppi vengono disegnati, e chi li disegna è la stessa colonna
// che disegna Surface — nessun contenitore in mezzo, nessun tetto da rispettare.
// «ADVANCED» E UN CAPITOLO CHE SI APRE, e si legge come gli altri capitoli: stessa misura,
// stesso peso, stesso grigio. Prima era una riga in grassetto sopra una linea, cioe un terzo
// livello di titolo in una colonna che ne ha gia due — e chi scorreva non sapeva se stava
// leggendo un capitolo o una card.
const AdvancedSpine = styled.button<{ $open: boolean }>`
    display: flex;
    align-items: center;
    gap: 6px;
    width: 100%;
    margin-top: 14px;
    border: 0;
    padding: 0 2px 6px;
    background: transparent;
    color: ${panel.inkMuted};
    font-family: inherit;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    cursor: pointer;

    svg {
        width: 10px;
        height: 10px;
        transform: rotate(${(props) => (props.$open ? '90deg' : '0deg')});
        transition: transform 120ms ease;
    }
`;

// SI RICORDA PER MATERIALE, e vive fuori dal componente perché il componente si rimonta a ogni
// cambio di soggetto: tenuto nello stato, «Advanced» si richiuderebbe sotto le dita ogni volta
// che si torna su un materiale che si era appena aperto.
const advancedOpenByMaterial = new Map<string, boolean>();

// IL FLUSSO SCELTO A MANO SI RICORDA, per la stessa ragione. La modalità si DEDUCE dal
// materiale (vedi `detectFinishMode`), ma passare a Separate su un materiale che non ha ancora
// caricato niente non lascia traccia su di lui: senza questa memoria la card tornerebbe a ORM
// al primo ridisegno, sotto le dita di chi sta per caricare la prima mappa.
const finishModeByMaterial = new Map<string, FinishMode>();

// E SI RICORDA ANCHE CIÒ CHE SI STACCA cambiando sponda, per rimetterlo se si torna indietro.
//
// Staccare è obbligatorio, non è una pulizia: lo shader MOLTIPLICA in fila — la ruvidità presa
// dal verde del pacchetto e poi di nuovo dalla mappa separata — e due sponde accese insieme
// darebbero un materiale improvvisamente opaco senza che si capisca perché.
//
// La memoria dura quanto la sessione: dopo un salvataggio il materiale è quello nuovo e non
// c'è più niente da rimettere.
//
// E IL CAMBIO NON CHIEDE CONFERMA (utente, 2026-09-09). Una c’era, e contava le mappe che si
// staccavano: ma nella sponda dove si arriva quelle mappe non ci sono mai state — la loro
// casella è vuota — e tornando indietro ricompaiono. Una domanda che nel caso normale non
// protegge da niente insegna solo a rispondere «sì» senza leggere.

type FinishSide = Record<string, unknown>;
const finishMemoryByMaterial = new Map<string, Partial<Record<FinishMode, FinishSide>>>();

// IL FLUSSO SI LEGGE DAI FLAG, PRIMA CHE DALLE MAPPE. Una spunta di canale accesa dice che la
// mappa del metallo è un pacchetto: in Separate sono sempre tutte spente, perché lì il metallo si
// legge dal rosso di una mappa in grigio. Guardare solo se c'era un'ambient sbagliava proprio sul
// caso più comune — un glTF importato porta la stessa ORM anche come ambient, per l'occlusione —
// e il materiale si apriva in Separate, con la ORM nello slot «Metallic».
const detectFinishMode = (material: any): FinishMode => {
    const packed =
        material?.metallicTexture &&
        (material.useRoughnessFromMetallicTextureGreen ||
            material.useRoughnessFromMetallicTextureAlpha ||
            material.useMetallnessFromMetallicTextureBlue ||
            material.useAmbientOcclusionFromMetallicTextureRed);

    if (packed) return 'orm';
    return material?.microSurfaceTexture || material?.ambientTexture ? 'separate' : 'orm';
};

/** Il flusso di un materiale: quello scelto a mano, se c'è, altrimenti quello che si legge. */
const modeOf = (material: any): FinishMode => finishModeByMaterial.get(material?.id ?? material?.name ?? '') ?? detectFinishMode(material);

// L'OCCLUSIONE DEL PACCHETTO SI DICE COME LA DICE IL glTF: la stessa immagine anche nello slot
// ambient, letta in grigio — cioè dal rosso. Si faceva col flag del rosso, che a vedersi è
// identico, ma quel flag lo legge solo lo shader: l'export lo ignora, e l'AR partiva senza
// occlusione. Lo slot ambient invece il glTF lo conosce, e con la stessa immagine ne esce la sua
// forma canonica — una texture sola, per la ruvidità, il metallo e l'occlusione.
//
// Il flag del rosso resta letto, per i materiali salvati prima: la spunta li mostra accesi, e
// toccarla li porta nella forma nuova.
const ormOcclusionOn = (material: any) => Boolean(material?.ambientTexture || material?.useAmbientOcclusionFromMetallicTextureRed);

const setOrmOcclusion = (material: any, on: boolean) => {
    material.useAmbientOcclusionFromMetallicTextureRed = false;
    material.ambientTexture = on ? (material.metallicTexture ?? null) : null;
    if (on) material.useAmbientInGrayScale = true;
};

// LE DUE SPONDE, DICHIARATE. `metallicTexture` sta in tutte e due e non è una svista: nel
// pacchetto è la mappa con i tre canali, in Separate è la mappa del solo metallo letta dal
// rosso. Stesso campo, due significati — e proprio per questo va staccato passando di là.
// Lo stesso vale per `ambientTexture`: nel pacchetto è la ORM stessa, letta per l'occlusione
// (vedi `setOrmOcclusion`); in Separate è la mappa dell'occlusione.
const FINISH_SIDES: Record<FinishMode, string[]> = {
    orm: [
        'metallicTexture',
        'useRoughnessFromMetallicTextureGreen',
        'useRoughnessFromMetallicTextureAlpha',
        'useMetallnessFromMetallicTextureBlue',
        'useAmbientOcclusionFromMetallicTextureRed',
        'ambientTexture',
        'useAmbientInGrayScale'
    ],
    separate: ['metallicTexture', 'microSurfaceTexture', 'ambientTexture', 'useAmbientInGrayScale']
};

const readFinishSide = (material: any, mode: FinishMode): FinishSide => Object.fromEntries(FINISH_SIDES[mode].map((field) => [field, material?.[field]]));

const writeFinishSide = (material: any, mode: FinishMode, side?: FinishSide) => {
    FINISH_SIDES[mode].forEach((field) => {
        const value = side ? side[field] : undefined;
        material[field] = value ?? (field.endsWith('Texture') ? null : false);
    });
};

const MaterialOverlaySimple: React.FC<MaterialOverlaySimpleProps> = ({
    material,
    onMaterialUpdated,
    onNameChanged,
    additionalFeatures,
    onClose,
    onPreviewUpdated,
    onRevealObject,
    onSceneChanged
}) => {
    const [zkProduct, zkScene, sceneManager, sceneEditor, designManager] = usePreview3DStore((x) => [
        x.zkProduct,
        x.zkScene,
        x.sceneManager,
        x.sceneEditor,
        x.designManager
    ]);

    // Spostato getTextureUrls dentro il componente per accedere a zkScene
    // OGNI STRADA PORTA A UN INDIRIZZO RELATIVO ALLA SCENA, non solo l’ultima. Le prime
    // quattro tornavano la stringa così com’era — il percorso dentro la cartella della scena —
    // e il browser la risolveva contro il backoffice: da lì le miniature rotte accanto a
    // texture che in 3D si vedono benissimo. La risoluzione è una sola e vale per tutte.
    const getTextureUrls = useCallback(
        (texture: BABYLON.BaseTexture): string[] => {
            const rootUrl = zkScene?.get('rootUrl') || '';
            const resolve = (url: string) => sceneAssetUrl(rootUrl, url);
            const isTextureWithFiles = (texture: BABYLON.BaseTexture): texture is BABYLON.BaseTexture & { _files: string[] } =>
                '_files' in texture && !!texture._files && Array.isArray(texture._files);

            const isTextureWithUrl = (texture: BABYLON.BaseTexture): texture is BABYLON.BaseTexture & { url: string } => 'url' in texture && !!texture.url;

            if (isTextureWithFiles(texture) && texture._files.length > 1) {
                return texture._files.map(resolve);
            }
            if (texture.metadata && texture.metadata.fileUrl) {
                return [resolve(texture.metadata.fileUrl)];
            }
            if (isTextureWithUrl(texture) && texture.url.startsWith('data:')) {
                return [texture.url];
            }
            if (texture.getInternalTexture && texture.getInternalTexture()?.url) {
                const textureUrl = texture.getInternalTexture()?.url;
                return textureUrl ? [resolve(textureUrl)] : [];
            }
            if (isTextureWithUrl(texture) && texture.url) {
                // Check if materialMapping exists before accessing its properties
                if (
                    (texture as any).materialMapping &&
                    (texture as any).materialMapping.libraryMaterialID &&
                    (texture as any).materialMapping.libraryMaterialRootUrl
                ) {
                    const materialMapping = (texture as any).materialMapping;
                    const idx = texture.url.indexOf(materialMapping.libraryMaterialID.replace(/-/g, ''));
                    if (idx !== -1) return [materialMapping.libraryMaterialRootUrl + texture.url];
                }
                return [resolve(texture.url)];
            }
            return [];
        },
        [zkScene]
    );

    const [loadingTextures, setLoadingTextures] = useState<Record<string, boolean>>({});
    const [, setUpdate] = useState(false);
    const materialsPropertiesCreator = useMaterialsPropertiesCreator();
    const { showDialog, closeDialog } = useDialogManager();
    const { ecommerceName, sellerMarketplace, updateSubscription } = useSubscriptionData();

    // L'anteprima del materiale aperto. Si riscatta solo quando qualcuno lo chiede:
    // rigenerarla a ogni modifica di proprietà era il disegno di prima, ed è il motivo per cui
    // l'avevano spenta — ogni scatto smonta e rimonta l'environment della scena, e farlo
    // mentre trascini uno slider è insostenibile.
    const [preview, setPreview] = useState<string | null>(null);
    const [isCapturingPreview, setIsCapturingPreview] = useState(false);

    useEffect(() => {
        setPreview(material?.id ? readMaterialPreview(sceneEditor, material.id) : null);
    }, [material?.id, sceneEditor]);

    // Due passi e non uno: lo scatto si mostra appena c'è, e se il caricamento non riesce lo
    // si dice. L'hai chiesto tu, quindi hai diritto sia di vederlo sia di sapere che non
    // sopravviverà al salvataggio.
    const capturePreview = useCallback(async () => {
        if (!sceneEditor || !material?.id || isCapturingPreview) return;

        setIsCapturingPreview(true);
        const dataUrl = await captureMaterialPreview(sceneEditor, material.id);

        if (!dataUrl) {
            setIsCapturingPreview(false);
            showErrorNotification({ message: T._('Could not generate the material preview.', 'Admin') });
            return;
        }

        setPreview(dataUrl);
        onPreviewUpdated?.(material.id, dataUrl);

        const url = await saveMaterialPreview(sceneEditor, material.id, dataUrl);
        setIsCapturingPreview(false);

        if (!url) showErrorNotification({ message: T._('The preview was generated but could not be saved.', 'Admin') });
    }, [sceneEditor, material, isCapturingPreview, onPreviewUpdated]);

    const renderDebounced = useCallback(
        debounce(() => designManager?.render(), 100),
        [designManager]
    );

    const updateState = useCallback(
        <T extends BABYLON.Material>(material: T, refreshMaterials?: boolean) => {
            const isDesignMaterial = zkProduct
                ?.get('scenes')
                .at(0)
                .get('meshSides')
                ?.toArray()
                .some((meshSide) => meshSide.get('materialID') === material.id);

            // Always re render because the base design material uses the same metallic/roughness of the base material
            if (isDesignMaterial) {
                renderDebounced();
            }

            setUpdate((prev) => !prev);
            sceneEditor?.render();
        },
        [sceneManager, zkProduct, renderDebounced, sceneEditor]
    );

    const isTextureLoading = useCallback(
        <T extends BABYLON.Material>(material: T, type: string, mobile: boolean) => {
            return loadingTextures[generateTextureUniqueId(material, type, mobile)];
        },
        [loadingTextures]
    );

    const uploadTexture = useCallback(
        async <T extends BABYLON.Material>(material: T, type: string, mobile: boolean, files: FileList) => {
            if (!sceneEditor) {
                throw new Error('Scene editor is not available');
            }

            if (files.length === 0) {
                throw new Error('No files selected');
            }

            if (type === 'reflection' && !additionalFeatures?.customEnvironmentTexture) {
                // Chiudo l'overlay prima di mostrare la dialog
                if (onClose) {
                    onClose();
                }

                showDialog(
                    'Upgrade_Plan',
                    <UpgradeBanner
                        marketPlace={sellerMarketplace ?? undefined}
                        message={stringFormat(T._('Your current plan does not include this feature. Upgrade now to access it.', 'Admin'), 'Grow')}
                        onUpgradeCompleted={async () => {
                            updateSubscription();
                        }}
                    />
                );
                return;
            }

            const uniqueId = generateTextureUniqueId(material, type, mobile);
            setLoadingTextures((prev) => ({ ...prev, [uniqueId]: true }));

            // SE LA MAPPA VA NEL PACCHETTO si decide prima di caricarla: dopo c'è già, e non si
            // saprebbe più se è arrivata in uno slot vuoto o al posto di un'altra.
            const pbr = material as any;
            const intoPack = type === 'metallic' && !mobile && modeOf(pbr) === 'orm';
            const replaced: any[] = intoPack ? [pbr.metallicTexture, pbr.ambientTexture] : [];

            try {
                if (mobile) {
                    await sceneEditor.loadMobileTextureFilesAsync(Array.from(files), material, type, false);
                } else {
                    await sceneEditor.loadTextureFilesAsync(Array.from(files), material, type, false);
                }

                if (intoPack) {
                    // UNA ORM IN UNO SLOT VUOTO ARRIVA CON I TRE CANALI ACCESI, perché è questo che
                    // vuol dire ORM: occlusione nel rosso, ruvidità nel verde, metallo nel blu. Con le
                    // spunte spente lo shader leggeva il metallo dal rosso e ignorava il verde — e
                    // l'export, che le spunte non le guarda, mostrava in AR un altro materiale.
                    // Al posto di un'altra mappa, invece, restano le spunte scelte, e l'occlusione, se
                    // c'era, passa sulla mappa nuova.
                    if (!replaced[0]) {
                        pbr.useRoughnessFromMetallicTextureGreen = true;
                        pbr.useMetallnessFromMetallicTextureBlue = true;
                        pbr.useRoughnessFromMetallicTextureAlpha = false;
                        setOrmOcclusion(pbr, true);
                    } else if (ormOcclusionOn(pbr)) {
                        setOrmOcclusion(pbr, true);
                    }

                    // La mappa di prima il motore la tiene in vita finché sta nello slot ambient: ora
                    // che non la usa più nessuno, si libera.
                    replaced.forEach((texture) => {
                        if (texture && sceneEditor.scene?.textures?.includes(texture) && !sceneEditor.isTextureUsed(texture)) texture.dispose();
                    });
                }
            } catch (error) {
                console.error(error);
                throw new Error('Failed to upload texture', {
                    cause: error
                });
            } finally {
                setLoadingTextures((prev) => ({ ...prev, [uniqueId]: false }));
                updateState(material);
                onSceneChanged?.();
            }
        },
        [sceneEditor, updateState, onSceneChanged]
    );

    const removeTexture = useCallback(
        async <T extends BABYLON.Material>(material: T, type: string, mobile: boolean) => {
            if (!sceneEditor) {
                throw new Error('Scene editor is not available');
            }

            // TOGLIERE LA ORM TOGLIE ANCHE LA SUA OCCLUSIONE: nello slot ambient c'è la stessa
            // immagine, e da sola resterebbe un'AO senza più la mappa da cui viene. Il flusso si
            // legge prima di togliere: dopo, senza la mappa, un'ambient rimasta direbbe Separate.
            const fromPack = type === 'metallic' && !mobile && modeOf(material) === 'orm';

            if (mobile) {
                sceneEditor.removeMaterialMobileTexture(material, type);
            } else {
                sceneEditor.removeMaterialTexture(material, type);
                if (fromPack && (material as any).ambientTexture) sceneEditor.removeMaterialTexture(material, 'ambient');
            }

            updateState(material);

            // IL CESTINO È UN PULSANTE, e un pulsante non emette `change`: togliere una mappa
            // cambiava il materiale senza che nessuno lo segnasse da salvare.
            onSceneChanged?.();
        },
        [sceneEditor, updateState, onSceneChanged]
    );

    // DOVE STA LA VERSIONE MOBILE DI UNA TEXTURE, e sono due posti soli: `metadata.mobileUrl` per
    // un'immagine, `metadata.mobileFiles` per una cube map. Li riempiono `setMobileTextureImage` e
    // `setMobileCubeTextureImages` del motore, e un terzo posto non esiste — per questo la ricerca
    // finisce qui invece di continuare altrove.
    //
    // SI RISOLVE COME QUELLA DESKTOP, ed è il punto: la mappa desktop ha cinque modi di trovare la
    // propria immagine e la mobile ne aveva uno, con una regola tutta sua — «se non comincia per
    // http, incollaci davanti la radice». Un `blob:` o un `data:`, che è la forma di una texture
    // appena caricata e non ancora salvata, diventavano così un indirizzo che non esiste: nessuna
    // anteprima, e nessun modo di capire perché. La radice ora la mette `sceneAssetUrl`, che
    // riconosce lo schema — ed è la stessa che risolve la mappa desktop.
    //
    // (Resta aperto di là il caso dei materiali importati dalla libreria, la cui versione mobile
    //  vuole la radice del materiale e non quella della scena: https://www.wrike.com/open.htm?id=1712281262)
    const getMobileTextureUrls = useCallback(
        (texture: any): string[] => {
            const rootUrl = zkScene?.get('rootUrl') || '';
            const files: string[] = texture?.metadata?.mobileFiles ?? [];

            if (files.length > 0) {
                return files.map((file) => sceneAssetUrl(rootUrl, file));
            }

            const url: string | undefined = texture?.metadata?.mobileUrl;
            return url ? [sceneAssetUrl(rootUrl, url)] : [];
        },
        [zkScene]
    );

    const getTexturePreviewUrl = useCallback(
        <T extends BABYLON.Material>(material: T, type: string, mobile: boolean) => {
            if (!sceneEditor || !zkScene) {
                return null;
            }

            const texture = sceneEditor.getMaterialTexture(material, type);
            if (!texture) {
                return null;
            }

            const urls = mobile ? getMobileTextureUrls(texture) : getTextureUrls(texture);

            return urls[0] ?? null;
        },
        [sceneEditor, zkScene, getTextureUrls, getMobileTextureUrls]
    );

    // Funzione stabile per l'onChange del nome materiale
    const handleMaterialNameChange = useCallback(
        (newValue: string) => {
            if (!material) {
                return;
            }

            // Aggiorna il nome direttamente sul materiale BABYLON
            material.name = newValue || 'Material';

            // Chiama updateState per aggiornare l'UI dell'overlay
            updateState(material);

            // Notifica immediatamente il cambiamento del nome al parent
            if (onNameChanged) {
                onNameChanged(material.name);
            }

            // Notifica il sistema che i materiali sono stati aggiornati per sincronizzare l'albero
            if (onMaterialUpdated) {
                setTimeout(() => {
                    onMaterialUpdated(material);
                }, 50); // Small delay to ensure material name is fully updated
            }
        },
        [material, updateState, onMaterialUpdated, onNameChanged]
    );

    // Helpers per il materialsPropertiesCreator
    const helpers = useMemo(() => {
        if (!sceneEditor || !sceneManager) {
            return null;
        }
        return {
            sceneEditor,
            sceneManager,
            updateState,
            isTextureLoading,
            uploadTexture,
            removeTexture,
            getTexturePreviewUrl,
            handleMaterialNameChange
        };
    }, [sceneEditor, sceneManager, updateState, isTextureLoading, uploadTexture, removeTexture, getTexturePreviewUrl, handleMaterialNameChange]);

    // La spina «Advanced» si ricorda di essere aperta, e la memoria è per materiale: aprirla su
    // uno non dice niente su quello che si guarderà dopo.
    const materialKey = material?.id ?? material?.name ?? '';
    const [advancedOpen, setAdvancedOpen] = useState(false);

    useEffect(() => setAdvancedOpen(advancedOpenByMaterial.get(materialKey) ?? false), [materialKey]);

    const toggleAdvanced = useCallback(() => {
        setAdvancedOpen((open) => {
            advancedOpenByMaterial.set(materialKey, !open);
            return !open;
        });
    }, [materialKey]);

    const [finishMode, setFinishMode] = useState<FinishMode>('orm');

    useEffect(() => setFinishMode(modeOf(material)), [materialKey]);

    const changeFinishMode = useCallback(
        (next: FinishMode) => {
            if (next === finishMode) return;

            const previous: FinishMode = finishMode;
            const remembered = finishMemoryByMaterial.get(materialKey) ?? {};

            remembered[previous] = readFinishSide(material, previous);
            finishMemoryByMaterial.set(materialKey, remembered);

            writeFinishSide(material, previous, undefined);
            writeFinishSide(material, next, remembered[next]);

            // IL CURSORE È UN GUADAGNO, non un valore assoluto: moltiplica la mappa, quindi a zero
            // la mappa non si vede e sembra rotta. Entrando in Separate la ruvidità parte da 1, che
            // è il valore neutro di una moltiplicazione (deciso in call, 2026-09-09).
            if (next === 'separate' && !material.roughness) material.roughness = 1;

            finishModeByMaterial.set(materialKey, next);
            setFinishMode(next);
            updateState(material, true);

            // IL COMMUTATORE È FATTO DI PULSANTI, e un pulsante non emette `change`: il pennarello
            // «da salvare» non lo sentiva. Ma cambiare flusso stacca delle mappe dal materiale, e
            // senza segnarlo «Save» rispondeva «nulla da salvare».
            onSceneChanged?.();
        },
        [finishMode, material, materialKey, updateState, onSceneChanged]
    );

    // Funzione per verificare se il materiale è Standard (non supportato)
    const isStandardMaterial = useCallback((material: any) => {
        // Un materiale Standard ha diffuseColor ma NON ha proprietà PBR avanzate
        return (
            material &&
            material.diffuseColor !== undefined &&
            (material.clearCoat === undefined || !material.clearCoat?.isEnabled) &&
            (material.metallic === undefined || material.metallic === 0) &&
            (material.roughness === undefined || material.roughness === 0) &&
            // Oppure verifica il tipo direttamente se disponibile
            (material instanceof BABYLON?.StandardMaterial || material.getClassName?.() === 'StandardMaterial')
        );
    }, []);

    if (!material || !helpers) {
        return (
            <InspectorShell kind='material' name={material?.name ?? ''}>
                <Spinner />
            </InspectorShell>
        );
    }

    const kindOfMaterial = material.getClassName?.() ?? T._('Material', 'Admin');

    // A QUALI OGGETTI È ADDOSSO. È la domanda che ci si fa guardando un materiale — «se lo cambio,
    // che cosa ridipingo?» — e la risposta la sa il motore: `getMeshesUsingMaterial` guarda anche
    // dentro i MultiMaterial, cioè le mesh che ne portano più d'uno e di cui questo è una parte.
    //
    // SI RICONTA A OGNI DISEGNO, e non è uno spreco: è un giro sulle mesh della scena. Una lista
    // tenuta da parte direbbe invece il conto di prima, perché l'assegnazione si cambia da un ALTRO
    // pannello — lo slot di una mesh — e da lì nessuno viene ad avvisare questo.
    //
    // ZERO OGGETTI, NIENTE PASTIGLIA: «0 objects» è la risposta a una domanda che nessuno ha fatto.
    const meshesUsingMaterial: any[] = sceneEditor?.getMeshesUsingMaterial?.(material.id) ?? [];

    const usedBy: InspectorUsage[] | undefined = meshesUsingMaterial.length
        ? [
              {
                  label: stringFormat(
                      meshesUsingMaterial.length === 1 ? T._('{0} object', 'Admin') : T._('{0} objects', 'Admin'),
                      meshesUsingMaterial.length.toString()
                  ),
                  current: meshesUsingMaterial.find((mesh) => sceneEditor?.isMeshSelected?.(mesh))?.id,
                  entries: meshesUsingMaterial.map((mesh): PropertiesPanelPickerOption => {
                      // I TRIANGOLI SONO UNA DIVISIONE, non un dato da chiedere a qualcuno: gli indici
                      // vanno a tre a tre. Ed è il dato che distingue due «Zone» che si chiamano quasi
                      // uguale — quale delle due è il pezzo grande.
                      const indices = typeof mesh.getTotalIndices === 'function' ? mesh.getTotalIndices() : 0;
                      const triangles = indices ? Math.round(indices / 3) : 0;

                      return {
                          value: mesh.id,
                          label: mesh.name,
                          caption: triangles ? `${triangles.toLocaleString()} ${T._('triangles', 'Admin')}` : undefined,
                          mark: <MeshNodeIcon />
                      };
                  }),
                  // ANDARCI SONO DUE MOVIMENTI: la colonna di sinistra torna sugli oggetti e la riga
                  // giusta si apre. Li fa `revealTarget` — lo stesso giro della freccia «dove sta» —
                  // e qui si dice soltanto dove si vuole andare.
                  onPick: (value) => onRevealObject?.(String(value))
              }
          ]
        : undefined;

    // Se è un materiale Standard, mostra solo il messaggio di non supportato
    // La sfera fotografata è la stessa che la lista mostra nella tessera: qui si vede com'è venuta,
    // e premendola si riscatta dopo aver cambiato il materiale.
    const portrait = (
        <PreviewThumb
            type='button'
            $image={preview}
            $color={(material as any)?.albedoColor?.toHexString?.()}
            title={isCapturingPreview ? T._('Generating...', 'Admin') : T._('Regenerate preview', 'Admin')}
            aria-label={isCapturingPreview ? T._('Generating...', 'Admin') : T._('Regenerate preview', 'Admin')}
            data-busy={isCapturingPreview}
            onClick={capturePreview}
            disabled={isCapturingPreview || !sceneEditor}
        >
            <PreviewShot>
                <CamerasIcon />
            </PreviewShot>
        </PreviewThumb>
    );

    if (isStandardMaterial(material)) {
        return (
            <InspectorShell kind='material' name={material.name} specs={kindOfMaterial} usage={usedBy} onRename={handleMaterialNameChange} glyph={portrait}>
                <UnsupportedNote>{T._('Materiale non supportato', 'Admin')}</UnsupportedNote>
            </InspectorShell>
        );
    }

    // LE PROPRIETÀ SONO QUELLE DI SEMPRE, RIMESSE IN ORDINE. `buildPbrMaterialPanel` non le
    // costruisce: prende quelle che il pannello condiviso costruisce già e le colloca nei
    // capitoli. Ciò che la tabella non nomina non compare — vedi pbrMaterialLayout.ts.
    const properties = materialsPropertiesCreator(material, helpers);

    // LA RIORGANIZZAZIONE È DEL SOLO `PBRMaterial` (utente, 2026-08-20). Gli altri due tipi PBR
    // hanno proprietà con altri nomi: passarli da questa tabella non li riorganizzerebbe, li
    // svuoterebbe — nessun id combacia, e resterebbe un pannello vuoto. «Ignorarli» vuol dire
    // lasciarli come sono, quindi si mostra l'elenco piatto di prima.
    const isClassicPbr = material instanceof BABYLON.PBRMaterial;
    const sections = isClassicPbr
        ? buildPbrMaterialPanel(properties, buildPbrExtraProperties(material as BABYLON.PBRMaterial, helpers), {
              finish: finishMode,
              switcher: {
                  type: 'segmented',
                  id: 'finish-mode',
                  options: [
                      { value: 'orm', label: 'ORM' },
                      { value: 'separate', label: T._('Separate', 'Admin') }
                  ],
                  value: finishMode,
                  onChange: (value: string) => changeFinishMode(value as FinishMode),
                  ariaLabel: T._('Finish maps', 'Admin')
              },
              // LA SPUNTA DELL'OCCLUSIONE, NEL PACCHETTO, NON ACCENDE PIÙ IL FLAG DEL ROSSO: mette la
              // stessa mappa nello slot ambient. Il perché sta in `setOrmOcclusion`.
              overrides: {
                  'use-ambient-occlusion-from-red': (property) =>
                      property.type === 'checkbox'
                          ? {
                                ...property,
                                checked: ormOcclusionOn(material),
                                onChange: (checked: boolean) => {
                                    setOrmOcclusion(material, checked);
                                    updateState(material);
                                    onSceneChanged?.();
                                }
                            }
                          : property
              }
          })
        : [];
    const advancedSections = sections.filter((section) => section.advanced);

    return (
        <InspectorShell kind='material' name={material.name} specs={kindOfMaterial} usage={usedBy} onRename={handleMaterialNameChange} glyph={portrait}>
            {/* IN EVIDENZA RESTA LA SOLA SURFACE — colore, finitura, trasparenza: ciò che si
                tocca a ogni materiale. Gli altri capitoli entrano interi sotto la spina, e il
                pannello parte con tre gruppi invece che con diciotto. */}
            {!isClassicPbr && <PropertiesPanel properties={properties} />}

            {sections
                .filter((section) => !section.advanced)
                .map((section) => (
                    <PropertiesPanelSection key={section.id} title={section.label}>
                        <PropertiesPanel properties={section.groups} />
                    </PropertiesPanelSection>
                ))}

            {advancedSections.length > 0 && (
                <AdvancedSpine type='button' $open={advancedOpen} aria-expanded={advancedOpen} onClick={toggleAdvanced}>
                    <ChevronIcon />
                    <span>{T._('Advanced', 'Admin')}</span>
                </AdvancedSpine>
            )}

            {advancedOpen &&
                advancedSections.map((section) => (
                    <PropertiesPanelSection key={section.id} title={section.label}>
                        <PropertiesPanel properties={section.groups} />
                    </PropertiesPanelSection>
                ))}
        </InspectorShell>
    );
};

export default MaterialOverlaySimple;
